import React from 'react';
import { isModeLocked, type LockedModes, type ModeOption } from './modes';
import type { WorkspaceMode } from './urlState';

/**
 * Mode_Switch: segmented control in the Top_Bar.
 * Track #e9e7ef with 3px padding; active segment white with a soft shadow;
 * locked segments are disabled, use #b4adc4 text and carry the reason in `title`.
 * Locked segments never call `onChange`.
 *
 * Requirements: 3.2, 3.5.
 */
export interface ModeSwitchProps {
  modes: readonly ModeOption[];
  active: WorkspaceMode;
  /** Locked Modes mapped to the reason shown on hover. */
  locked?: LockedModes;
  onChange: (mode: WorkspaceMode) => void;
  className?: string;
}

export const ModeSwitch: React.FC<ModeSwitchProps> = ({
  modes,
  active,
  locked,
  onChange,
  className = '',
}) => (
  <div
    role="group"
    aria-label="Workspace mode"
    className={`inline-flex items-center gap-0.5 rounded-[10px] bg-[#e9e7ef] p-[3px] ${className}`.trim()}
  >
    {modes.map(({ id, label }) => {
      const isLocked = isModeLocked(locked, id);
      const isActive = id === active && !isLocked;
      const reason = isLocked ? locked?.[id] || 'Not available yet' : undefined;
      const stateClasses = isLocked
        ? 'cursor-not-allowed text-[#b4adc4]'
        : isActive
          ? 'bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
          : 'text-gray-600 hover:text-gray-900';
      return (
        <button
          key={id}
          type="button"
          data-mode={id}
          disabled={isLocked}
          title={reason}
          aria-pressed={isActive}
          onClick={() => {
            if (isLocked) return;
            onChange(id);
          }}
          className={`whitespace-nowrap rounded-[7px] px-3 py-1.5 text-[13px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] ${stateClasses}`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

export default ModeSwitch;
